import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { Loader2 } from 'lucide-react';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNotification } from '@/components/notifications';
import { getWorkflowList } from '@/services/workflow/get-workflow-list';
import {
  useCreateTrigger,
  useCreateTriggerSubscription,
  useDeleteTrigger,
} from '@/hooks/mutations/triggers/use-trigger-mutations';
import { TriggerConnection } from '@/services/triggers/types';
import { useWorkflowFileInputs } from '@/hooks/queries/triggers/use-workflow-file-inputs';
import {
  FileInputSelect,
  buildInputBinding,
  optionKey,
} from '@/pages/triggers/file-input-select';
import {
  DEFAULT_MAX_SIZE_BYTES,
  FILE_TYPE_GROUPS,
  MAX_SIZE_OPTIONS,
  POLL_INTERVAL_OPTIONS,
  mimeTypesForGroups,
} from './file-type-groups';
import { FolderCombobox } from './folder-combobox';

interface AddTriggerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId?: string;
  connection: TriggerConnection;
  // Opened from a workflow's own Triggers section: that workflow is the only
  // possible target, so the picker is hidden rather than defaulted. Attaching
  // other workflows is deliberately left to the Triggers page.
  lockedWorkflowId?: string;
  lockedWorkflowName?: string;
}

export const AddTriggerModal = ({
  open,
  onOpenChange,
  orgId,
  connection,
  lockedWorkflowId,
  lockedWorkflowName,
}: AddTriggerModalProps) => {
  const notification = useNotification();
  const [folderId, setFolderId] = useState('');
  const [folderName, setFolderName] = useState('');
  const [pickedWorkflowId, setPickedWorkflowId] = useState('');
  const workflowId = lockedWorkflowId ?? pickedWorkflowId;
  const [fileInputKey, setFileInputKey] = useState('');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [maxSizeBytes, setMaxSizeBytes] = useState(
    String(DEFAULT_MAX_SIZE_BYTES),
  );
  const [intervalSeconds, setIntervalSeconds] = useState('60');

  const createTrigger = useCreateTrigger(orgId);
  const createSubscription = useCreateTriggerSubscription(orgId);
  const deleteTrigger = useDeleteTrigger(orgId);

  useEffect(() => {
    if (!open) {
      setFolderId('');
      setFolderName('');
      setPickedWorkflowId('');
      setFileInputKey('');
      setGroupIds([]);
      setMaxSizeBytes(String(DEFAULT_MAX_SIZE_BYTES));
      setIntervalSeconds('60');
    }
  }, [open]);

  const workflowsQuery = useQuery({
    queryKey: ['triggerable-workflows', orgId],
    queryFn: () => getWorkflowList(orgId!),
    enabled: !!orgId && open && !lockedWorkflowId,
    staleTime: 60_000,
  });

  // Fetched per open, so a node added since the last open is reflected.
  const fileInputsQuery = useWorkflowFileInputs(
    orgId,
    workflowId || undefined,
    open,
  );
  const fileInputs = fileInputsQuery.data ?? [];
  const needsFileInputChoice = fileInputs.length > 1;
  // Never submit on a guess: without this the modal falls back to "auto-bind"
  // whenever the shape is unknown, and the backend rejects the binding instead.
  const inputsUnknown =
    !!workflowId && (fileInputsQuery.isPending || fileInputsQuery.isError);
  const hasNoFileInput = !!workflowId && !inputsUnknown && !fileInputs.length;

  const workflows = useMemo(
    () =>
      (workflowsQuery.data?.data?.workflows ?? []).filter(
        (w) =>
          w.user_role === 'workflow:owner' || w.user_role === 'workflow:editor',
      ),
    [workflowsQuery.data],
  );

  const selectedFolderName = folderName || folderId;

  const toggleGroup = (groupId: string, checked: boolean) =>
    setGroupIds((prev) =>
      checked ? [...prev, groupId] : prev.filter((id) => id !== groupId),
    );

  // deleteTrigger is the orphan cleanup after a failed attach; leaving Create
  // clickable while it runs lets a quick retry race it and strand extra rows.
  const isSaving =
    createTrigger.isPending ||
    createSubscription.isPending ||
    deleteTrigger.isPending;

  const handleSave = async () => {
    if (!folderId) {
      notification.warning?.('Choose a Drive folder');
      return;
    }
    if (!workflowId) {
      notification.warning?.('Choose a workflow to run');
      return;
    }
    if (inputsUnknown) {
      notification.warning?.(
        fileInputsQuery.isError
          ? "Could not read this workflow's inputs. Reopen and try again."
          : "Still reading this workflow's inputs — try again in a moment.",
      );
      return;
    }
    if (needsFileInputChoice && !fileInputKey) {
      notification.warning?.('Choose which node receives the file');
      return;
    }
    if (hasNoFileInput) {
      notification.warning?.(
        'That workflow has no file input, so there is nowhere to deliver the file',
      );
      return;
    }
    // Guard on the resolved list rather than the checkboxes: an unrecognised
    // group id resolves to nothing, and a null mime_types means "accept every
    // type" server-side — the opposite of what was selected.
    const mimeTypes = mimeTypesForGroups(groupIds);
    if (!mimeTypes.length) {
      notification.warning?.('Choose at least one file type');
      return;
    }
    try {
      const trigger = await createTrigger.mutateAsync({
        connection_id: connection.id,
        name: selectedFolderName || undefined,
        config: {
          folder_id: folderId,
          // Kept for display: the folder list is a live Drive read, so the row
          // would otherwise show a bare id once the picker cache expires.
          folder_name: selectedFolderName,
        },
        filters: {
          mime_types: mimeTypes,
          max_size_bytes: Number(maxSizeBytes),
        },
        interval_seconds: Number(intervalSeconds),
        // Bind the workflow before polling starts, so the first poll cannot
        // detect a file with nothing subscribed to run it.
        enabled: false,
      });
      try {
        await createSubscription.mutateAsync({
          triggerId: trigger.id,
          input: {
            target_ref: workflowId,
            input_binding: buildInputBinding(
              needsFileInputChoice
                ? fileInputs.find((i) => optionKey(i) === fileInputKey)
                : undefined,
            ),
          },
        });
      } catch (attachError) {
        // The trigger exists but runs nothing; leaving it would strand a paused
        // orphan row, and a retry would add another.
        await deleteTrigger.mutateAsync(trigger.id).catch((cleanupError) => {
          Sentry.captureException(cleanupError, {
            extra: { triggerId: trigger.id, reason: 'orphan cleanup failed' },
          });
        });
        throw attachError;
      }
      notification.success?.('Trigger created — enable it when you are ready');
      onOpenChange(false);
    } catch (error) {
      Sentry.captureException(error, {
        extra: { connectionId: connection.id, folderId, workflowId },
      });
      notification.error(
        error instanceof Error ? error.message : 'Could not create the trigger',
      );
    }
  };

  return (
    <Modal
      title='Watch a Drive folder'
      description='New files uploaded to this folder will start the workflow you choose.'
      isDialogOpen={open}
      setIsDialogOpen={onOpenChange}
      footer={
        <div className='flex justify-end gap-2'>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              isSaving || inputsUnknown || hasNoFileInput || !groupIds.length
            }
          >
            {isSaving && <Loader2 className='mr-2 size-4 animate-spin' />}
            Create trigger
          </Button>
        </div>
      }
    >
      <div className='space-y-4'>
        <div className='space-y-1.5'>
          <Label>Drive folder</Label>
          <FolderCombobox
            orgId={orgId}
            connectionId={connection.id}
            value={folderId}
            selectedName={folderName}
            onSelect={(id, name) => {
              setFolderId(id);
              setFolderName(name);
            }}
            enabled={open}
          />
          <p className='text-xs text-muted-foreground'>
            Your own folders and any shared with you. Folders in a Shared Drive
            are not supported yet.
          </p>
        </div>

        {lockedWorkflowId ? (
          <div className='space-y-1.5'>
            <Label>Workflow to run</Label>
            <p className='rounded-md border bg-muted/40 px-3 py-2 text-sm'>
              {lockedWorkflowName || 'This workflow'}
            </p>
            <p className='text-xs text-muted-foreground'>
              To run other workflows from this folder, attach them from
              Triggers.
            </p>
          </div>
        ) : (
          <div className='space-y-1.5'>
            <Label>Workflow to run</Label>
            <Select
              value={pickedWorkflowId}
              onValueChange={(next) => {
                setPickedWorkflowId(next);
                setFileInputKey('');
              }}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    workflowsQuery.isLoading
                      ? 'Loading workflows…'
                      : 'Select a workflow'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {workflows.map((w) => (
                  <SelectItem key={w.workflow_id} value={w.workflow_id}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className='text-xs text-muted-foreground'>
              The workflow needs a file input. You can attach more workflows to
              this folder afterwards.
            </p>
          </div>
        )}

        {needsFileInputChoice && (
          <div className='space-y-1.5'>
            <Label>Node that receives the file</Label>
            <FileInputSelect
              options={fileInputs}
              value={fileInputKey}
              onChange={setFileInputKey}
              disabled={isSaving}
            />
          </div>
        )}
        {fileInputsQuery.isError && (
          <p className='text-xs text-destructive'>
            Could not read this workflow&apos;s inputs, so the file has nowhere
            to go yet. Close and reopen this dialog to retry.
          </p>
        )}
        {hasNoFileInput && (
          <p className='text-xs text-destructive'>
            This workflow has no file input, so there is nowhere to deliver the
            file. Add a file input node to it first.
          </p>
        )}

        <div className='space-y-2'>
          <Label>File types that should start a run</Label>
          <div className='space-y-2'>
            {FILE_TYPE_GROUPS.map((group) => (
              <label
                key={group.id}
                className='flex items-center gap-2 text-sm font-normal'
              >
                <Checkbox
                  checked={groupIds.includes(group.id)}
                  onCheckedChange={(checked) =>
                    toggleGroup(group.id, checked === true)
                  }
                />
                <span>{group.label}</span>
              </label>
            ))}
          </div>
          {groupIds.length ? (
            <p className='text-xs text-muted-foreground'>
              Only these file types will start a run.
            </p>
          ) : (
            <p className='text-xs text-destructive'>
              Choose at least one file type.
            </p>
          )}
        </div>

        <div className='grid grid-cols-2 gap-3'>
          <div className='space-y-1.5'>
            <Label>Maximum file size</Label>
            <Select value={maxSizeBytes} onValueChange={setMaxSizeBytes}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MAX_SIZE_OPTIONS.map((option) => (
                  <SelectItem key={option.bytes} value={String(option.bytes)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className='space-y-1.5'>
            <Label>Check for new files</Label>
            <Select value={intervalSeconds} onValueChange={setIntervalSeconds}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {POLL_INTERVAL_OPTIONS.map((option) => (
                  <SelectItem
                    key={option.seconds}
                    value={String(option.seconds)}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </Modal>
  );
};
